import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import { useInView } from 'react-intersection-observer';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import SlideInModal from 'src/app/components/Elements/SlideInModal';
import { Typography } from 'src/app/components/Elements/Typography';
import { segmentTrack } from 'src/app/state/actions';
import { getCurrentPathname, getCurrentPathParams } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import { theme } from 'src/styles/theme';

interface LearnMoreModalProps {
  showModal: boolean;
  title: string;
  body: ReactNode;
  handleCloseModal: () => void;
}

const LearnMoreModal = ({ showModal = false, title, body, handleCloseModal }: LearnMoreModalProps) => {
  const pathname = useSelector(getCurrentPathname);
  const pathparams = useSelector(getCurrentPathParams);
  const dispatch = useDispatch();

  const { inView, ref } = useInView({ threshold: 0.25 });

  useEffect(() => {
    if (inView) {
      dispatch(segmentTrack(UNIFIED.viewLearnMore, { pagepath: `${pathname}${pathparams}` }));
    }
  }, [dispatch, inView, pathname, pathparams]);

  const isMobile = useMediaQuery(theme.breakpoints.mediumDown);

  const slideDirection = isMobile ? 'up' : 'right';

  return (
    <SlideInModal isShown={showModal} onClose={handleCloseModal} direction={slideDirection}>
      <div ref={ref}>
        <Typography variant="h3" marginBottom={'s2'}>
          {title}
        </Typography>
        <Typography variant="body">{body}</Typography>
      </div>
    </SlideInModal>
  );
};

export default LearnMoreModal;
